import React, { useState, useEffect, useCallback } from 'react';
import UploadPanel from '../components/Admin/UploadPanel';
import DocumentList from '../components/Admin/DocumentList';
import AnalyticsTab from '../components/Admin/AnalyticsTab';
import CuratedAnswersTab from '../components/Admin/CuratedAnswersTab';
import FeedbackTab from '../components/Admin/FeedbackTab';
import { getDocuments } from '../api/client';
import { useAuth } from '../context/AuthContext';

// Tab icons — Lucide-style monoline SVGs.  Each takes a `color` so we can
// pivot between the active (white) and inactive (slate) states without
// duplicating the markup.
const ICON_SIZE = 17;
const baseIcon = (color) => ({
  width: ICON_SIZE,
  height: ICON_SIZE,
  stroke: color,
  fill: 'none',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  display: 'block',
});

const DocumentsIcon = ({ color }) => (
  // file with folded corner + content lines
  <svg viewBox="0 0 24 24" style={baseIcon(color)} aria-hidden="true">
    <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
    <polyline points="14 3 14 9 20 9" />
    <line x1="8" y1="13" x2="16" y2="13" />
    <line x1="8" y1="17" x2="14" y2="17" />
  </svg>
);

const CuratedIcon = ({ color }) => (
  // sparkles / check — represents SME-vetted content
  <svg viewBox="0 0 24 24" style={baseIcon(color)} aria-hidden="true">
    <path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6z" />
    <path d="M19 14l.9 2.1L22 17l-2.1.9L19 20l-.9-2.1L16 17l2.1-.9z" />
  </svg>
);

const FeedbackIcon = ({ color }) => (
  // speech bubble with dots
  <svg viewBox="0 0 24 24" style={baseIcon(color)} aria-hidden="true">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8z" />
  </svg>
);

const AnalyticsIcon = ({ color }) => (
  // bar chart
  <svg viewBox="0 0 24 24" style={baseIcon(color)} aria-hidden="true">
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6"  y1="20" x2="6"  y2="14" />
    <line x1="3"  y1="20" x2="21" y2="20" />
  </svg>
);

export default function AdminPage() {
  const { isAdmin, isViewer } = useAuth();
  const TABS = isAdmin
    ? [
        { key: 'documents', label: 'Documents',       Icon: DocumentsIcon },
        { key: 'curated',   label: 'Curated Answers', Icon: CuratedIcon   },
        { key: 'feedback',  label: 'Feedback',        Icon: FeedbackIcon  },
        { key: 'analytics', label: 'Analytics',       Icon: AnalyticsIcon },
      ]
    : [
        { key: 'feedback',  label: 'Feedback',  Icon: FeedbackIcon  },
        { key: 'analytics', label: 'Analytics', Icon: AnalyticsIcon },
      ];
  const [tab, setTab] = useState(isAdmin ? 'documents' : 'analytics');
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchDocs = useCallback(async () => {
    try {
      const res = await getDocuments();
      setDocuments(res.data.documents || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab !== 'documents') return;
    fetchDocs();
    const interval = setInterval(fetchDocs, 5000);
    return () => clearInterval(interval);
  }, [fetchDocs, tab]);

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '26px', fontWeight: 800, marginBottom: '6px' }}>
          {isViewer ? 'Analytics Dashboard' : 'Admin Panel'}
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
          {isViewer
            ? 'Read-only view — usage, knowledge gaps, and quality metrics.'
            : 'Manage documents, monitor usage, and find knowledge gaps.'}
        </p>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '28px',
        padding: '10px',
        background: 'linear-gradient(135deg, #ffffff 0%, #f0fafa 100%)',
        border: '1px solid rgba(29,156,156,0.22)',
        borderRadius: '18px',
        width: 'fit-content',
        boxShadow: '0 22px 50px rgba(13,79,82,0.22), 0 10px 22px rgba(13,79,82,0.12), 0 2px 6px rgba(13,79,82,0.06)',
      }}>
        {TABS.map((t) => {
          const active = tab === t.key;
          const iconColor = active ? '#ffffff' : '#475569';
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '12px 22px',
                borderRadius: '14px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: active ? 700 : 600,
                background: active ? 'linear-gradient(135deg, #1d9c9c, #0d7377)' : '#ffffff',
                color: active ? '#ffffff' : '#475569',
                boxShadow: active
                  ? '0 18px 36px rgba(13,115,119,0.40), 0 8px 16px rgba(13,115,119,0.25), 0 2px 4px rgba(0,0,0,0.08)'
                  : '0 10px 22px rgba(13,79,82,0.14), 0 4px 8px rgba(13,79,82,0.08), inset 0 0 0 1px rgba(29,156,156,0.10)',
                transition: 'all 0.22s ease',
                transform: active ? 'translateY(-1px)' : 'none',
              }}
            >
              <t.Icon color={iconColor} />
              {t.label}
            </button>
          );
        })}
      </div>

      {error && tab === 'documents' && (
        <div style={{
          padding: '12px 16px',
          background: 'rgba(252,129,129,0.1)',
          border: '1px solid var(--error)',
          borderRadius: 'var(--radius-sm)',
          color: 'var(--error)',
          marginBottom: '20px',
          fontSize: '13px',
        }}>
          {error}
        </div>
      )}

      {tab === 'documents' && (
        <>
          <UploadPanel onUploadDone={fetchDocs} />
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
              Loading documents…
            </div>
          ) : (
            <DocumentList documents={documents} onRefresh={fetchDocs} />
          )}
        </>
      )}

      {tab === 'curated' && <CuratedAnswersTab />}

      {tab === 'feedback' && <FeedbackTab />}

      {tab === 'analytics' && <AnalyticsTab />}
    </div>
  );
}
